/**
 * 行程实况 —— `/api/orders/{orderNo}/live` 等三个端点（S8，2026-09-20 后端交接）。
 *
 * ★ **这不是「司机位置」那么一个小组件**：一个接口就回整张卡要的全部内容 ——
 *   状态文案、行程、司机、车的位置、按钮亮不亮，全在一个 `payload` 里。
 *
 * ★★ 四条贯穿整个模块的纪律，做界面前必须先认下（全部来自 S8 交接说明）：
 *
 *  1. 🔴 **停止条件由后端给。** `terminal` / `nextPollSeconds` 说停就停、说隔多久就隔多久。
 *     ⚠⚠ **前端绝不维护一份「哪些 state 算结束」** —— 那份副本迟早会漂，而漂了
 *     **没人会发现**：症状只是「行程结束两小时了还在每 10 秒打一次」，分摊在成千上万
 *     个已结束的行程上，只有看监控的人才注意得到。判定一律走
 *     {@link shouldStopLivePolling} / {@link nextLivePollMs}。
 *  2. 🔴 **拿不到位置不是错误**，有四种，处置完全不同 —— 见 {@link RIDE_LOCATION_REASONS}。
 *     前三种**整张卡照常显示**，只是地图上没有车。把整张卡变成错误页，
 *     等于为了一个缺失的字段丢掉那一屏上绝大多数好的信息。
 *  3. 🔴 **三个字段的「没有值」是【键不出现】，不是 0。** `locationTimeMs` / `etaMinutes` /
 *     `edaMeters`。判 0 的后果分别是「56 年前」「还有 0 分钟」「还有 0 米」——
 *     三条都是**不报错**的错。所以这三个字段声明成 `optional()` 且**绝不给默认值**。
 *  4. 🔴 **算「几秒前」的基准是 {@link orderLiveSchema} 的 `serverTimeMs`，不是本机时间。**
 *     `locationTimeMs` 是打车平台上报的绝对时刻；用户机器时钟偏两分钟，页面就开始胡说。
 *     走 {@link locationAgeSeconds}，别在组件里写 `Date.now()`。
 *
 * ⚠ `payload` 是**真正的嵌套对象，不用 `JSON.parse`** —— 与订单详情的 `domainPayload`
 *   （那个是字符串，要 parse）**不是一回事**，别弄反。取消问价回的是订单详情的形状，
 *   所以那一个**要** parse：见 {@link parseRideCancelQuote}。
 */
import { z } from 'zod'
import type { OrderDetail } from './orders'

/**
 * 拿不到位置的四种理由。
 *
 * 🔴 **四种的处置完全不同，尤其最后一种：**
 *
 * | 值 | 意思 | 卡片怎么做 |
 * |---|---|---|
 * | `NOT_IN_TRIP` | 还在派单中，或行程已结束 | 正常显示状态与行程，**不显示车标**。最常见，不是问题 |
 * | `SUPPLIER_UNAVAILABLE` | 暂时够不着打车平台 | **沿用上一次的位置** + 标一句「位置更新中」。别清空地图 |
 * | `API_DISABLED` | 后端开关没开 | 同上。⚠ 这是**配置问题**，联调遇到要报给后端 |
 * | `CREDENTIAL_INVALID` | **用户的打车账号没绑 / 失效了** | 停轮询（后端同时给 `nextPollSeconds: 0`）+ 引导重新绑定 |
 *
 * ⚠ **故意不是 `z.enum`**（同 `cards.ts` 的 `reason`）：收到第五个值时整个响应
 *   `safeParse` 会失败，于是「车开到哪了」在界面上直接变成一个错误 ——
 *   而那一刻状态、行程、司机、按钮其实全是好的。认不出的一律按
 *   「不显示车标、但卡片照常」处理，这在四种里都成立。
 */
export const RIDE_LOCATION_REASONS = [
  'NOT_IN_TRIP',
  'SUPPLIER_UNAVAILABLE',
  'API_DISABLED',
  'CREDENTIAL_INVALID',
] as const
export type RideLocationReason = (typeof RIDE_LOCATION_REASONS)[number]

/**
 * 这次拿不到位置是**账号的问题**（要去重新绑定，见 `cards.ts` 的 `ride_token_required`）。
 *
 * ⚠ 只有它一个要把用户引去别的地方；另外三种都是「等下一次就好了」。
 */
export function isCredentialInvalidReason(reason: string | null | undefined): boolean {
  return reason === 'CREDENTIAL_INVALID'
}

/**
 * 这次拿不到位置，要不要**沿用上一次的坐标**。
 *
 * ⚠⚠ `SUPPLIER_UNAVAILABLE` / `API_DISABLED` 是「暂时够不着」，车还在原地附近跑 ——
 *   清空地图会让用户以为车消失了。而 `NOT_IN_TRIP` 是**真的没有车可显示**
 *   （还在派单，或行程已结束），这时留着上一次的坐标就是在显示一辆并不存在的车。
 * ⚠ 认不出的理由按**不沿用**处理：显示一个陈旧坐标比不显示更糟。
 */
export function keepsLastKnownLocation(reason: string | null | undefined): boolean {
  return reason === 'SUPPLIER_UNAVAILABLE' || reason === 'API_DISABLED'
}

/** 一个点（起点 / 终点）。 */
export const ridePlaceSchema = z.looseObject({
  /** 地点名，如「怀德商务大厦」。 */
  name: z.string().nullable().optional(),
  /** 推荐上/下车点名，如「北门」。⚠ **可能没有这个键** —— 没有就只显示 `name`，别编一个。 */
  boardName: z.string().nullable().optional(),
  /**
   * ⚠ 这是**车真正要去的那个点**（后端已处理好：有推荐上下车点就用它，没有才回退到
   *   POI 中心）。所以画地图一律用这两个数，别拿 `name` 再去地理编码一次。
   */
  lat: z.number().optional(),
  lng: z.number().optional(),
})
export type RidePlace = z.infer<typeof ridePlaceSchema>

/** 行程静态面。⚠ 首屏之后几乎不变 —— 这个分组是**有意的**，不是随手分的。 */
export const rideTripSchema = z.looseObject({
  origin: ridePlaceSchema.optional(),
  destination: ridePlaceSchema.optional(),
  carDesc: z.string().nullable().optional(),
  supplierName: z.string().nullable().optional(),
  /**
   * ⚠ 这里是**数字**（`18.40`），与 `ride_confirm` 卡片上那个**字符串**
   *   `estimated_price_yuan`（可能是「打表计价」四个字）**不是同一个字段**，别混用渲染代码。
   * ⚠ 仍然是**预估价**：钱由打车平台行程结束后直接向用户收，我方不经手（见 `orders.ts`
   *   的 `isSupplierSettledDomain`）。所以必须跟着 `priceNote` 一起显示。
   */
  estimatedPriceYuan: z.number().nullable().optional(),
  /** 后端原话，如「预估价,实际按行程计费;费用由打车平台直接向你收取」。⚠ 别在前端重写这句。 */
  priceNote: z.string().nullable().optional(),
})
export type RideTrip = z.infer<typeof rideTripSchema>

/** 司机与车。⚠ 每次都在变（派单期间整个对象可能还没有）。 */
export const rideDriverSchema = z.looseObject({
  name: z.string().nullable().optional(),
  /** 评分，**字符串**（如 `"4.7"`）。⚠ 别当数字算。 */
  level: z.string().nullable().optional(),
  plate: z.string().nullable().optional(),
  vehicle: z.string().nullable().optional(),
})
export type RideDriver = z.infer<typeof rideDriverSchema>

/**
 * 车的位置。
 *
 * 🔴 **`etaMinutes` / `edaMeters` / `locationTimeMs` 三个字段没有值时【整个键不出现】。**
 *   所以它们是 `optional()` 且**绝不能给 `.default(0)`** —— 判 0 的三种后果
 *   （「56 年前」「还有 0 分钟」「还有 0 米」）都是不报错的错。见本文件头注第 3 条。
 */
export const rideLocationSchema = z.looseObject({
  /** ⚠ `false` **不是错误**，有四种理由 —— 见 {@link RIDE_LOCATION_REASONS}。 */
  available: z.boolean().default(false),
  /** ⚠ 仅 `available === false` 时才有。收任意字符串，理由见 {@link RIDE_LOCATION_REASONS}。 */
  unavailableReason: z.string().nullable().optional(),
  lat: z.number().optional(),
  lng: z.number().optional(),
  /** 车头朝向，0–360。缺 = 不知道朝哪，车标就别转。 */
  directionDegrees: z.number().optional(),
  /** ⚠ 缺 = **没有 ETA**，不是 0 分钟。 */
  etaMinutes: z.number().optional(),
  /** ⚠ 缺 = **没有距离**，不是 0 米。 */
  edaMeters: z.number().optional(),
  /**
   * 打车平台上报这个坐标的**绝对时刻**（毫秒）。
   * ⚠⚠ 缺 = **没有时效可说**，不是 1970 年。算「几秒前」一律走 {@link locationAgeSeconds}。
   */
  locationTimeMs: z.number().optional(),
})
export type RideLocation = z.infer<typeof rideLocationSchema>

/**
 * 按钮亮不亮。
 *
 * 🔴 **`supplierTel` 是【运力商客服】，不是司机手机号。** 打车平台不提供司机号码
 *   （官方文档把「联系司机」列为不支持）。⇒ 按钮请写「联系客服」，
 *   **不要写「联系司机」** —— 写了就是承诺一件做不到的事。
 */
export const rideActionsSchema = z.looseObject({
  /** 取消入口亮不亮。⚠ 亮了也**不能一键取消** —— 必须先问价，见 {@link parseRideCancelQuote}。 */
  cancellable: z.boolean().default(false),
  /**
   * 免费取消的**时刻字符串**（如 `"16:07"`），不是时间戳、不是倒计时秒数。
   * ⚠ 直接显示，别丢进 `new Date()`。
   */
  freeCancelTime: z.string().nullable().optional(),
  /** 🔴 运力商**客服**电话。见本 schema 头注。 */
  supplierTel: z.string().nullable().optional(),
})
export type RideActions = z.infer<typeof rideActionsSchema>

/**
 * 实况 `payload`。
 *
 * ⚠⚠ **这是真正的嵌套对象，不用 `JSON.parse`。**（订单详情的 `domainPayload` 是字符串，
 *   那个要 parse —— 两者不一样，别弄反。）
 */
export const ridePayloadSchema = z.looseObject({
  trip: rideTripSchema.optional(),
  driver: rideDriverSchema.optional(),
  location: rideLocationSchema.optional(),
  actions: rideActionsSchema.optional(),
})
export type RidePayload = z.infer<typeof ridePayloadSchema>

/**
 * `GET /api/orders/{orderNo}/live` 的 `data`。
 *
 * ⚠ `state` 用 `z.string()`：这是**我方**状态机（见 `orders.ts` 的 `ORDER_STATES`），
 *   后端加一个状态时前端不该整页解析失败 —— 而且**根本不需要读它**：
 *   要显示的字用 `statusText`，要不要停用 `terminal`。
 */
export const orderLiveSchema = z.looseObject({
  orderNo: z.string().min(1),
  state: z.string().min(1),
  /** 已经组好的中文状态，如「司机正在赶来」。⚠ 别按 `state` 在前端另拼一份。 */
  statusText: z.string().nullable().optional(),
  /** 🔴 行程结束/取消/没叫上。⇒ **卡片定格，别再问了。** */
  terminal: z.boolean().default(false),
  /**
   * 🔴 下次隔多久再问（秒）。后端按状态下发（等车 10 / 行程中 30 / 拿不到位置 60 / 停 0），
   *   **改策略不用等前端发版**。
   * ⚠ 缺键**不当 0**（那会停掉一个在途的行程）—— 走 {@link DEFAULT_LIVE_POLL_SECONDS}。
   */
  nextPollSeconds: z.number().optional(),
  /** 🔴 算「几秒前」的**唯一基准**。见本文件头注第 4 条。 */
  serverTimeMs: z.number().optional(),
  payload: ridePayloadSchema.optional(),
})
export type OrderLive = z.infer<typeof orderLiveSchema>

/**
 * 后端没给 `nextPollSeconds` 时的间隔。
 *
 * ⚠ 这**不是**前端的轮询策略，只是一个「字段缺了别把在途行程停掉」的兜底 ——
 *   策略永远以后端下发的为准。
 */
export const DEFAULT_LIVE_POLL_SECONDS = 10

/**
 * 🔴 **该不该停。判据只有这两个，别加第三个。**
 *
 * ```js
 * if (shouldStopLivePolling(data)) stop()
 * else setTimeout(poll, nextLivePollMs(data))
 * ```
 *
 * ⚠⚠ 尤其**不要**在这里补一句「或者 state 是 ORDER_COMPLETED / ORDER_CANCELLED」——
 *   那就是本文件头注第 1 条说的那份会漂的副本。状态机在后端。
 */
export function shouldStopLivePolling(live: OrderLive): boolean {
  return live.terminal || live.nextPollSeconds === 0
}

/** 下一次隔多久（毫秒）。⚠ 只在 {@link shouldStopLivePolling} 为 false 时有意义。 */
export function nextLivePollMs(live: OrderLive): number {
  const seconds = live.nextPollSeconds ?? DEFAULT_LIVE_POLL_SECONDS
  return Math.max(1, seconds) * 1000
}

/**
 * 坐标是「几秒前」上报的。
 *
 * 🔴 **基准是 `serverTimeMs`，不是 `Date.now()`** —— 用户机器时钟偏两分钟，
 *   页面就开始胡说（本文件头注第 4 条）。
 * 🔴 **`locationTimeMs` 缺键时回 `null`，调用方据此【不显示时效】** ——
 *   判 0 的话 `serverTimeMs - 0` 会算出「56 年前」。
 *
 * ⚠ 两个字段任缺其一就回 `null`：没有可靠基准时，不说比说错好。
 */
export function locationAgeSeconds(live: OrderLive): number | null {
  const at = live.payload?.location?.locationTimeMs
  const now = live.serverTimeMs
  if (at === undefined || now === undefined) return null
  return Math.max(0, Math.round((now - at) / 1000))
}

/**
 * 取消问价的结果（`POST /api/orders/{orderNo}/cancel-quote`）。
 *
 * ⚠⚠ 它藏在订单详情的 **`domainPayload` 字符串**里，要 `JSON.parse` ——
 *   与 `/live` 的 `payload`（真对象）**不是一回事**。
 */
export const rideCancelQuoteSchema = z.looseObject({
  /** ⚠ **字符串**金额，如 `"5.00"`。`"0.00"` = **此刻取消免费**。 */
  cancel_fee: z.string().nullable().optional(),
  /** ⚠ **时刻字符串**（如 `"16:07"`），不是时间戳。与 `/live` 的 `actions.freeCancelTime` 同源。 */
  free_cancel_time: z.string().nullable().optional(),
})
export type RideCancelQuote = z.infer<typeof rideCancelQuoteSchema>

/**
 * 从取消问价的响应里挖出取消费。
 *
 * ⚠ 解不开就回 `null`，调用方据此说「问不到取消费」——**绝不能当成免费**：
 *   那笔钱是打车平台**直接从用户账上扣**的，我方不经手也退不了。
 *   把「不知道」显示成「免费」，就是让用户在不知道要收多少钱的情况下点了取消。
 */
export function parseRideCancelQuote(detail: OrderDetail): RideCancelQuote | null {
  if (!detail.domainPayload) return null
  try {
    const parsed = rideCancelQuoteSchema.safeParse(JSON.parse(detail.domainPayload))
    return parsed.success ? parsed.data : null
  } catch {
    // ⚠ parse 失败只降级、不抛：一段坏 JSON 不该让「取消」这条路整个断掉
    return null
  }
}

/**
 * 此刻取消是不是免费。
 *
 * ⚠ 判的是**数值为 0**，不是字符串等于 `"0.00"` —— 后端某天回 `"0"` 或 `"0.0"`
 *   时，字面比较会把免费说成收费（不报错，只是多说一句吓人的话）。
 * ⚠ 认不出的值（null / 空串 / 非数字）回 `false` = **按收费处理**：
 *   见 {@link parseRideCancelQuote} 的头注，不知道时绝不说免费。
 */
export function isFreeCancelFee(fee: string | null | undefined): boolean {
  if (fee === null || fee === undefined || fee.trim() === '') return false
  const value = Number(fee)
  return Number.isFinite(value) && value === 0
}
